import { colors, radius, spacings } from "@/assets/themes";
import {
  Block,
  Body14,
  Body16,
  Button,
  Divider,
  H2,
  H5,
  Icon,
  List,
  Modal,
} from "@/components";
import { CardBase } from "@/components/Styles/Base";
import { BUTTON, LIST } from "@/components/Styles/variants";
import { useBreakpoints } from "@/modules/hooks";
import useJob from "@/modules/hooks/useJob";
import { useForcePrepaidIds } from "@/modules/routes";
import { CESU, WALLET } from "@/pages/common/payment-methods/constants";
import { getPaymentMethodBadge } from "@/pages/common/payment-methods/utils";
import { ACCOUNT_PAYMENT_METHODS_PATH } from "@/pages/dashboard/config/routes";
import {
  DEFERRED_PREPAYMENT,
  DEFERRED_PREPAYMENT_STATUS,
  formatCardNumber,
} from "@/utils";
import polyglot from "@/utils/polyglot";
import moment from "moment";
import { useMemo, useState } from "react";
import { getSelectedOffers } from "../utils";

const PrepaidBanners = () => {
  const { data } = useJob();
  return getSelectedOffers(data.accepted_offers)
    ?.filter(
      (offer) =>
        offer.payment_type === DEFERRED_PREPAYMENT &&
        !!offer.prepayment_informations
    )
    .map((offer) => <PrepaidBanner offer={offer} key={offer.id} />);
};

const PrepaidBanner = ({ offer }) => {
  const { pendingPrepaidIds } = useJob();
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const forcePrepaidIds = useForcePrepaidIds();

  const handleForcePrepaid = () => {
    forcePrepaidIds.mutate(pendingPrepaidIds);
  };

  const breakpoints = useBreakpoints();
  const isPaid =
    offer.prepayment_informations.prepayment_status ===
    DEFERRED_PREPAYMENT_STATUS.SUCCEEDED;
  const isError =
    offer.prepayment_informations.prepayment_status ===
    DEFERRED_PREPAYMENT_STATUS.FAILED;

  const formattedDate = useMemo(() => {
    if (offer.prepayment_informations.prepayment_at) {
      return moment(offer.prepayment_informations.prepayment_at).format(
        "D MMM [à] HH[h]mm"
      );
    }
    return null;
  }, [offer.prepayment_informations.prepayment_at]);

  const bannerWordings = useMemo(() => {
    if (isPaid) {
      return polyglot.t("job.prepaid_date_paid_compact", {
        amount: polyglot.toSmartCurrency(offer.price),
        date: formattedDate,
      });
    }
    return polyglot.t("job.prepaid_date_not_paid_compact", {
      amount: polyglot.toSmartCurrency(offer.price),
      date: formattedDate,
    });
  }, [isPaid, formattedDate, offer]);

  return (
    <Block marginTop={spacings.ml}>
      <CardBase
        flat
        border={breakpoints.get({ xs: true, md: !!isError })}
        css={`
          overflow: hidden;
        `}
      >
        {isError ? (
          <Block
            paddingX={spacings.m}
            paddingY={spacings.sm}
            borderRadius={radius.ml}
            backgroundColor={colors.dangerLight}
          >
            <List.Item
              divider={false}
              size={LIST.SIZE.COMPACT}
              LeftComponent={() => (
                <Icon.Large name="minus-circle-solid" color={colors.danger} />
              )}
            >
              <Body16 strong>
                {polyglot.t("job.prepaid_error_first_name", {
                  first_name: offer.jobber.first_name,
                })}
              </Body16>
              <Body14
                css={`
                  font-weight: var(--font-weight-regular);
                `}
              >
                {polyglot.t("job.prepaid_error_description_compact", {
                  amount: polyglot.toSmartCurrency(offer.price),
                })}
              </Body14>
              <Block marginTop={spacings.xxs}>
                <Button.Link
                  onClick={() => setIsDetailsModalOpen(true)}
                  accentColor={colors.body}
                  css={`
                    padding: 0;
                    font-size: var(--font-size-body14);
                    min-height: initial;
                  `}
                >
                  {polyglot.t("common.find_out_more")}
                </Button.Link>
              </Block>
            </List.Item>
            <Block
              display="flex"
              gap={spacings.xs}
              flexDirection="column"
              marginTop={spacings.s}
            >
              <Button.Small
                block
                kind={BUTTON.KIND.SECONDARY}
                shape={BUTTON.SHAPE.PILL}
                css={`
                  color: ${colors.body}!important;
                `}
                onClick={handleForcePrepaid}
                isLoading={forcePrepaidIds.isLoading}
              >
                {polyglot.t("common.retry")}
              </Button.Small>
              <Button.Small
                block
                css={`
                  color: ${colors.body}!important;
                  background-color: ${colors.transparent}!important;
                `}
                kind={BUTTON.KIND.MINIMAL}
                shape={BUTTON.SHAPE.PILL}
                href={ACCOUNT_PAYMENT_METHODS_PATH}
              >
                {polyglot.t("job.add_payment_method")}
              </Button.Small>
            </Block>
          </Block>
        ) : (
          <List.Item
            divider={false}
            LeftComponent={() => (
              <>
                {isPaid ? (
                  <Icon.Large
                    name="check-circle-solid"
                    color={colors.success}
                  />
                ) : (
                  <Icon.Large name="credit-card" />
                )}
              </>
            )}
            withGutters
          >
            <Body16 strong>
              {polyglot.t("job.prepaid_first_name", {
                first_name: offer.jobber.first_name,
              })}
            </Body16>
            <Body16 color={colors.muted}>{bannerWordings}</Body16>
            <Block marginTop={spacings.xxs}>
              <Button.Link
                onClick={() => setIsDetailsModalOpen(true)}
                accentColor={colors.body}
                css={`
                  padding: 0;
                  font-size: var(--font-size-body14);
                  min-height: initial;
                `}
              >
                {polyglot.t("common.find_out_more")}
              </Button.Link>
            </Block>
          </List.Item>
        )}
      </CardBase>
      <PrepaidBannersPaymentInfoModal
        offer={offer}
        isPaid={isPaid}
        isOpen={isDetailsModalOpen}
        close={() => setIsDetailsModalOpen(false)}
      />
    </Block>
  );
};

export default PrepaidBanners;

const PrepaidBannersPaymentInfoModal = ({ offer, isOpen, close, isPaid }) => (
  <Modal.XSmall isOpen={isOpen} onClose={close}>
    <Modal.Item.Header onClose={close}>
      <Modal.Item.Title as={H2}>
        {polyglot.t("job.prepaid_informations")}
      </Modal.Item.Title>
    </Modal.Item.Header>
    <Modal.Item.Wrapper>
      <CardBase flat border>
        <Block
          display="flex"
          flexDirection="column"
          gap={spacings.s}
          paddingTop={spacings.sm}
          paddingBottom={spacings.s}
        >
          {offer.prepayment_informations.pricing.debit > 0 && (
            <List.Item
              size={LIST.SIZE.COMPACT}
              withGutters
              divider={false}
              RightComponent={() => (
                <Body16>
                  {polyglot.toSmartCurrency(
                    offer.prepayment_informations.pricing.debit
                  )}
                </Body16>
              )}
              LeftComponent={() => (
                <img
                  src={getPaymentMethodBadge(
                    offer.prepayment_informations.mangopay_card_kind
                  )}
                  alt=""
                />
              )}
            >
              <Body16>
                {offer.prepayment_informations?.mangopay_card_alias
                  ? formatCardNumber(
                      offer.prepayment_informations?.mangopay_card_alias
                    )
                  : polyglot.t("payment_method.payment_card")}
              </Body16>
            </List.Item>
          )}
          {offer.prepayment_informations.pricing.wallet > 0 && (
            <List.Item
              size={LIST.SIZE.COMPACT}
              withGutters
              divider={false}
              RightComponent={() => (
                <Body16>
                  {polyglot.toSmartCurrency(
                    offer.prepayment_informations.pricing.wallet
                  )}
                </Body16>
              )}
              LeftComponent={() => (
                <img
                  src={getPaymentMethodBadge(WALLET)}
                  alt={polyglot.t("wallet.pot")}
                />
              )}
            >
              <Body16>{polyglot.t("wallet.pot")}</Body16>
            </List.Item>
          )}
          {offer.prepayment_informations.pricing.cesu > 0 && (
            <List.Item
              size={LIST.SIZE.COMPACT}
              withGutters
              divider={false}
              RightComponent={() => (
                <Body16>
                  {polyglot.toSmartCurrency(
                    offer.prepayment_informations.pricing.cesu
                  )}
                </Body16>
              )}
              LeftComponent={() => (
                <img
                  src={getPaymentMethodBadge(CESU)}
                  alt={polyglot.t("wallet.cesu")}
                />
              )}
            >
              <Body16>{polyglot.t("common.cesu")}</Body16>
            </List.Item>
          )}
        </Block>
        <Block marginX={spacings.m}>
          <Divider.Cell />
        </Block>

        <List.Item
          withGutters
          divider={false}
          RightComponent={() => (
            <H5 strong>
              {polyglot.toSmartCurrency(
                offer.prepayment_informations.pricing.total
              )}
            </H5>
          )}
        >
          <H5 strong>{polyglot.t("common.total")}</H5>
        </List.Item>
      </CardBase>
      <Block marginTop={spacings.m}>
        <Body14 color={colors.muted}>
          {isPaid
            ? polyglot.t("job.prepaid_helper_prepaid")
            : polyglot.t("job.prepaid_helper")}
        </Body14>
      </Block>
    </Modal.Item.Wrapper>
  </Modal.XSmall>
);
